/**
 * Identity endpoints (API catalogue §6; amendments A-API-003, A-API-004, A-API-007).
 * Typed calls only; no business rules live here.
 */
import { api } from '@/app/api/client'

export interface MembershipSummary {
  tenant_id: string
  slug: string
  display_name: string
  membership_status: 'active' | 'suspended' | 'invited'
  tenant_status: string
  logo_url: string | null
  /** Set when the tenant is reachable through a support session, not a membership. */
  via?: 'support'
}
/** The caller's open platform support session (FR-SUP-001): the tenant it unlocks. */
interface SupportSessionSummary {
  id: string
  tenant_id: string
  slug: string
  display_name: string
  tenant_status: string
  write_allowed: boolean
  reason: string
  expires_at: string
}

export interface Me {
  id: string
  email: string
  username: string | null
  mfa_required: boolean
  mfa_enrolled: boolean
  platform_permissions: string[]
  password_change_required: boolean
  memberships: MembershipSummary[]
  support_session: SupportSessionSummary | null
}

export type LoginResult = { kind: 'ok'; me: Me } | { kind: 'mfa_required' }

export async function login(loginId: string, password: string): Promise<LoginResult> {
  const response = await api.post<Me | { status: 'mfa_required' }>('/auth/login', {
    login: loginId,
    password,
  })
  if (response.status === 202) return { kind: 'mfa_required' }
  return { kind: 'ok', me: response.data as Me }
}

export async function completeMfa(code: string): Promise<Me> {
  const response = await api.post<Me>('/auth/login/mfa', { code })
  return response.data
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout')
}

export async function fetchMe(): Promise<Me> {
  const response = await api.get<Me>('/me')
  return response.data
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
}

export async function requestPasswordReset(loginId: string): Promise<void> {
  await api.post('/auth/password-reset', { login: loginId })
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  await api.post('/auth/password-reset/confirm', { token, new_password: newPassword })
}

export async function acceptInvitation(token: string, password: string): Promise<void> {
  await api.post('/auth/invitations/accept', { token, password })
}

export interface MfaEnrollment {
  device_id: string
  secret: string
  provisioning_uri: string
}

export async function beginMfaEnrollment(): Promise<MfaEnrollment> {
  const response = await api.post<MfaEnrollment>('/me/mfa/totp')
  return response.data
}

export async function confirmMfaEnrollment(code: string): Promise<string[]> {
  const response = await api.post<{ recovery_codes: string[] }>('/me/mfa/totp/confirm', { code })
  return response.data.recovery_codes
}

export async function disableMfa(currentPassword: string, code: string): Promise<void> {
  await api.delete('/me/mfa/totp', { data: { current_password: currentPassword, code } })
}

export interface UserSession {
  id: string
  created_at: string
  last_seen_at: string
  ip_address: string | null
  user_agent: string
  current: boolean
}

export async function listSessions(): Promise<UserSession[]> {
  const response = await api.get<UserSession[]>('/me/sessions')
  return response.data
}

export async function revokeSession(sessionId: string): Promise<void> {
  await api.delete(`/me/sessions/${sessionId}`)
}

export interface Grant {
  permission: string
  scope_type: string
  scope_ref_id: string | null
  role: string
  valid_until: string | null
}

/** BRD-057: the plan's modules (null = all) and any limited counts with the 90 % flag. */
interface PlanLimit {
  key: string
  title: string
  used: number
  limit: number
  warning: boolean
  reached: boolean
}
export interface PlanUsage {
  plan_name: string | null
  modules: string[] | null
  limits: PlanLimit[]
}
export async function fetchMyPermissions(): Promise<{
  grants: Grant[]
  plan: PlanUsage | null
  checkinRequired: boolean
  teamSigninPending: boolean
  leadsTeam: boolean
}> {
  const response = await api.get<{
    grants: Grant[]
    plan: PlanUsage | null
    checkin_required?: boolean
    team_signin_pending?: boolean
    leads_team?: boolean
  }>('/tenant/me/permissions')
  return {
    grants: response.data.grants,
    plan: response.data.plan ?? null,
    // BRD-077: this login must check in for the day before using the workspace
    checkinRequired: Boolean(response.data.checkin_required),
    // BRD-079: a checked-in lead whose team is not signed in yet today
    teamSigninPending: Boolean(response.data.team_signin_pending),
    // BRD-089: a team's designated lead sees the ticket queue and takes tickets
    leadsTeam: Boolean(response.data.leads_team),
  }
}
